import { useMapGetter, useStore } from 'dashboard/composables/store';
import {
  getAgentsByUpdatedPresence,
  getSortedAgentsByAvailability,
} from 'dashboard/helper/agentHelper';
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

/**
 * A composable function that provides a list of agents for assignment.
 *
 * @param {boolean} [includeNoneAgent=true] - Whether to include a 'None' agent option.
 * @param {Object} [options] - Options for the assignable agents list.
 * @param {boolean} [options.includeAgentBots=false] - Whether to include AgentBot assignees. Only pass this from surfaces that thread `assignee_type` through the assignment request.
 * @param {boolean} [options.restrictToAssignedTeam=false] - Customização Vision: quando a conversa tem um time, lista somente os agentes que pertencem a esse time (agent bots continuam na lista).
 * @returns {Object} An object containing the agents list and assignable agents.
 */
export function useAgentsList(
  includeNoneAgent = true,
  { includeAgentBots = false, restrictToAssignedTeam = false } = {}
) {
  const { t } = useI18n();
  const store = useStore();
  const currentUser = useMapGetter('getCurrentUser');
  const currentChat = useMapGetter('getSelectedChat');
  const currentAccountId = useMapGetter('getCurrentAccountId');
  const assignable = useMapGetter('inboxAssignableAgents/getAssignableAgents');

  const inboxId = computed(() => currentChat.value?.inbox_id);
  const isAgentSelected = computed(() => currentChat.value?.meta?.assignee);

  // Customização Vision: time atribuído à conversa (só usado com restrictToAssignedTeam)
  const assignedTeamId = computed(() =>
    restrictToAssignedTeam ? currentChat.value?.meta?.team?.id || null : null
  );

  // undefined = membros ainda não carregados; [] = carregado e sem membros
  const assignedTeamMembers = computed(() => {
    const teamId = assignedTeamId.value;
    if (!teamId) return undefined;
    return store?.state?.teamMembers?.records?.[teamId];
  });

  const teamMembersFailedFor = ref(null);

  if (restrictToAssignedTeam) {
    watch(
      assignedTeamId,
      async teamId => {
        if (!teamId || !store) return;
        try {
          await store.dispatch('teamMembers/get', { teamId });
        } catch (error) {
          // Sem os membros do time não bloqueamos a atribuição: volta para a lista completa
          teamMembersFailedFor.value = teamId;
        }
      },
      { immediate: true }
    );
  }

  /**
   * Creates a 'None' agent object
   * @returns {Object} None agent object
   */
  const createNoneAgent = () => ({
    confirmed: true,
    name: t('AGENT_MGMT.MULTI_SELECTOR.LIST.NONE') || 'None',
    id: 0,
    role: 'agent',
    account_id: 0,
    email: 'None',
  });

  /**
   * Customização Vision: mantém apenas agentes do time atribuído à conversa.
   * @param {Array} agents - Agentes atribuíveis da caixa de entrada
   * @returns {Array} Agentes filtrados
   */
  const filterByAssignedTeam = agents => {
    const teamId = assignedTeamId.value;
    if (!teamId) return agents;

    const members = assignedTeamMembers.value;
    if (!members) {
      return teamMembersFailedFor.value === teamId ? agents : [];
    }

    const memberIds = new Set(members.map(member => member.id));
    return agents.filter(
      agent => agent.assignee_type === 'AgentBot' || memberIds.has(agent.id)
    );
  };

  /**
   * @type {import('vue').ComputedRef<Array>}
   */
  const assignableAgents = computed(() => {
    const agents = inboxId.value
      ? assignable.value(inboxId.value, { includeAgentBots })
      : [];
    return filterByAssignedTeam(agents || []);
  });

  /**
   * @type {import('vue').ComputedRef<Array>}
   */
  const agentsList = computed(() => {
    const agents = (assignableAgents.value || []).map(agent =>
      !agent.name && agent.assignee_type === 'AgentBot'
        ? { ...agent, name: '-' }
        : agent
    );
    const agentsByUpdatedPresence = getAgentsByUpdatedPresence(
      agents,
      currentUser.value,
      currentAccountId.value
    );

    const filteredAgentsByAvailability = getSortedAgentsByAvailability(
      agentsByUpdatedPresence
    );

    return [
      ...(includeNoneAgent && isAgentSelected.value ? [createNoneAgent()] : []),
      ...filteredAgentsByAvailability,
    ];
  });

  return {
    agentsList,
    assignableAgents,
  };
}
